import type { AttemptController } from "./AttemptController";
import type { ParentSettingsV1, ParentStorageState, UnlockResult } from "./parentTypes";
import { serializeParentSettings } from "./parentValidation";
import { verifyPin as verifyStoredPin } from "./pinCrypto";

type VerifyPin = typeof verifyStoredPin;

interface PinVerificationCoordinatorOptions {
  attempts: Pick<AttemptController, "getStatus" | "recordFailure" | "reset">;
  readSettings: () => ParentStorageState;
  isDocumentHidden: () => boolean;
  verifyPin?: VerifyPin;
}

/**
 * Owns the lifetime of asynchronous PBKDF2 requests. Every new request or
 * security boundary advances the generation, so an older Promise can never
 * revive a Parent session after settings or page lifecycle state changed.
 */
export class PinVerificationCoordinator {
  private generation = 0;
  private active = true;
  private readonly verifyPin: VerifyPin;

  public constructor(private readonly options: PinVerificationCoordinatorOptions) {
    this.verifyPin = options.verifyPin ?? verifyStoredPin;
  }

  public invalidate(): void {
    this.generation += 1;
  }

  public destroy(): void {
    if (!this.active) return;
    this.active = false;
    this.invalidate();
  }

  public async verify(pin: string, expectedSettings: ParentSettingsV1): Promise<UnlockResult> {
    const generation = ++this.generation;
    const status = this.options.attempts.getStatus();
    if (!status.allowed) return { kind: "cooldown", blockedUntil: status.blockedUntil };

    const expectedFingerprint = serializeParentSettings(expectedSettings);
    const expectedPin = { ...expectedSettings.pin };
    const correct = await this.verifyPin(pin, expectedPin);

    if (!this.isCurrent(generation, expectedFingerprint)) return { kind: "unavailable" };

    // A cooldown created elsewhere while PBKDF2 was running also wins the race.
    const latestAttemptStatus = this.options.attempts.getStatus();
    if (!latestAttemptStatus.allowed) {
      return { kind: "cooldown", blockedUntil: latestAttemptStatus.blockedUntil };
    }
    if (!correct) {
      const failed = this.options.attempts.recordFailure();
      return failed.allowed
        ? { kind: "incorrect" }
        : { kind: "cooldown", blockedUntil: failed.blockedUntil };
    }
    this.options.attempts.reset();
    return { kind: "success" };
  }

  private isCurrent(generation: number, expectedFingerprint: string): boolean {
    if (!this.active || generation !== this.generation || this.options.isDocumentHidden()) {
      return false;
    }
    const current = this.options.readSettings();
    return (
      current.kind === "valid" && serializeParentSettings(current.settings) === expectedFingerprint
    );
  }
}
